import {useQuery, useQueryClient} from 'react-query';

import api from './../../lib/api';

/**
 * @typedef AssetViewerParams
 * @type {object}
 * @property {number} assetId - The id of the asset to be displayed!
 * @property {number} [collectionId] - The id of the playlist from which the asset was opened, can be nullish if the asset was opened from the search-page or from pinned items
 * @property {boolean} [isPublished] - Whether the playlist is publicly accessible or not
 */

async function selectAssetByID(id) {
	return api('get', `/api/me/assets/${id}`);
}

async function getAssetPlaylists(assetId) {
	return api('get', `/api/me/assets/${assetId}/parentPlaylists`, null, null);
}

/**
 * useFetchAsset - a hook for fetching and locally managing asset-data
 *
 * Uses {@link https://github.com/tannerlinsley/react-query|react-query} under the hood.
 * @param {number} id - The id of the asset to be fetched
 * @returns {*} the asset-data
 */
export function useFetchAsset(id) {
	const queryKey = ['fetch-asset', {id}];

	const config = {
		enabled: !!id,
	};

	async function queryFn() {
		const [asset, collections] = await Promise.all([
			selectAssetByID(+id),
			getAssetPlaylists(+id),
		]);
		return {
			...asset,
			collections,
		};
	}

	const result = useQuery({
		queryKey,
		queryFn,
		...config,
	});

	const queryClient = useQueryClient();

	const update = dataOrSetterFn =>
		queryClient.setQueryData(queryKey, dataOrSetterFn, config);

	return {
		...result,
		update,
	};
}
